import axios, { type AxiosInstance, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios'
import { AppError, ERROR_CODES, type ApiResponse, type RequestOptions } from '@cravo/core'
import { tokenManager } from './token-manager'
import { mapError } from './error-mapper'
import { shouldRetry, getRetryDelay, wait } from './retry'

interface ApiClientConfig {
  baseUrl: string
  timeout: number
  onUnauthorized?: () => void
}

const config: ApiClientConfig = {
  baseUrl: '/api/v1',
  timeout: 30_000,
}

export function configureApi(overrides: Partial<ApiClientConfig>): void {
  Object.assign(config, overrides)
  client.defaults.baseURL = config.baseUrl
  client.defaults.timeout = config.timeout
}

const client: AxiosInstance = axios.create({
  baseURL: config.baseUrl,
  timeout: config.timeout,
  headers: { 'Content-Type': 'application/json' },
})

// Request interceptor
client.interceptors.request.use((req: InternalAxiosRequestConfig) => {
  const skipAuth = (req as unknown as { _skipAuth?: boolean })._skipAuth
  if (!skipAuth) {
    const token = tokenManager.getAccessToken()
    if (token) {
      req.headers.Authorization = `Bearer ${token}`
    }
  }
  return req
})

// Token refresh queue
let isRefreshing = false
let refreshQueue: Array<{ resolve: (token: string) => void; reject: (error: AppError) => void }> = []

function enqueueRefreshWaiter(): Promise<string> {
  return new Promise((resolve, reject) => {
    refreshQueue.push({ resolve, reject })
  })
}

function resolveQueue(token: string): void {
  refreshQueue.forEach((w) => w.resolve(token))
  refreshQueue = []
}

function rejectQueue(error: AppError): void {
  refreshQueue.forEach((w) => w.reject(error))
  refreshQueue = []
}

async function attemptRefresh(): Promise<string> {
  const refreshToken = tokenManager.getRefreshToken()
  if (!refreshToken) {
    throw new AppError('No refresh token available', ERROR_CODES.REFRESH_FAILED, 401)
  }
  try {
    const response = await axios.post(
      `${config.baseUrl}/vendor/auth/refresh-token`,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' }, timeout: 10_000 },
    )
    const data = response.data.data || response.data
    const newToken = data.token
    const newRefreshToken = data.refreshToken
    if (!newToken) throw new Error('Invalid refresh response')
    tokenManager.set(newToken, newRefreshToken || refreshToken)
    return newToken
  } catch {
    tokenManager.clear()
    throw new AppError('Session expired. Please log in again.', ERROR_CODES.REFRESH_FAILED, 401)
  }
}

// Public endpoints that issue or rotate tokens themselves — refresh retry on
// these would loop forever. Mobile parity: refresh_token_interceptor.dart
// allowlists only public auth paths, NOT all of /auth/.
const PUBLIC_AUTH_PATHS = [
  '/vendor/auth/login',
  '/vendor/auth/register',
  '/vendor/auth/social-login',
  '/vendor/auth/verify-otp',
  '/vendor/auth/resend-otp',
  '/vendor/auth/send-otp',
  '/vendor/auth/forgot-password',
  '/vendor/auth/forgot-password/verify',
  '/vendor/auth/reset-password',
  '/vendor/auth/refresh',
  '/vendor/auth/check-availability',
  // Customer/rider equivalents kept for completeness
  '/auth/login',
  '/auth/register',
  '/auth/social-login',
  '/auth/verify-otp',
  '/auth/resend-otp',
  '/auth/send-otp',
  '/auth/forgot-password',
  '/auth/forgot-password/verify',
  '/auth/reset-password',
  '/auth/refresh',
]

function isPublicAuthPath(url?: string): boolean {
  if (!url) return false
  return PUBLIC_AUTH_PATHS.some((p) => url.endsWith(p) || url.includes(`${p}?`))
}

// Response interceptor
client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const skipRefresh =
      originalRequest?._skipRefresh ||
      originalRequest?._retried ||
      isPublicAuthPath(originalRequest?.url)

    if (error.response?.status === 401 && !skipRefresh) {
      originalRequest._retried = true
      if (isRefreshing) {
        try {
          const token = await enqueueRefreshWaiter()
          originalRequest.headers.Authorization = `Bearer ${token}`
          return client(originalRequest)
        } catch (refreshError) {
          return Promise.reject(refreshError)
        }
      }
      isRefreshing = true
      try {
        const newToken = await attemptRefresh()
        resolveQueue(newToken)
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return client(originalRequest)
      } catch (refreshError) {
        rejectQueue(refreshError as AppError)
        config.onUnauthorized?.()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }
    return Promise.reject(error)
  },
)

// Request method
async function request<T>(method: string, url: string, data?: unknown, options: RequestOptions = {}): Promise<T> {
  const axiosConfig: AxiosRequestConfig & { _skipAuth?: boolean; _skipRefresh?: boolean } = {
    method,
    url,
    params: options.params ? Object.fromEntries(Object.entries(options.params).filter(([, v]) => v != null)) : undefined,
    headers: options.headers,
    signal: options.signal,
    timeout: options.timeout,
    _skipAuth: options.skipAuth,
    _skipRefresh: options.skipRefresh,
  }

  if (data !== undefined) {
    if (data instanceof FormData) {
      axiosConfig.data = data
      axiosConfig.headers = { ...axiosConfig.headers, 'Content-Type': 'multipart/form-data' }
    } else {
      axiosConfig.data = data
    }
  }

  const maxRetries = options.retries ?? 3
  let lastError: unknown

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await client(axiosConfig)
      const body = response.data as ApiResponse<T>
      if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
        return body.data
      }
      return response.data as T
    } catch (error) {
      lastError = error
      if (error instanceof AppError) throw error
      if (attempt < maxRetries && shouldRetry(error as import('axios').AxiosError, attempt)) {
        await wait(getRetryDelay(attempt))
        continue
      }
      throw mapError(error)
    }
  }

  throw mapError(lastError)
}

export const api = {
  get<T>(url: string, options?: RequestOptions): Promise<T> {
    return request<T>('GET', url, undefined, options)
  },
  post<T>(url: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('POST', url, data, { retries: 0, ...options })
  },
  put<T>(url: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('PUT', url, data, { retries: 0, ...options })
  },
  patch<T>(url: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('PATCH', url, data, { retries: 0, ...options })
  },
  delete<T>(url: string, options?: RequestOptions): Promise<T> {
    return request<T>('DELETE', url, options?.data, { retries: 0, ...options })
  },
  upload<T>(url: string, formData: FormData, options?: RequestOptions): Promise<T> {
    return request<T>('POST', url, formData, { retries: 0, ...options })
  },
}

export function createAbortController(timeoutMs?: number): { signal: AbortSignal; cancel: () => void } {
  const controller = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined
  if (timeoutMs) {
    timer = setTimeout(() => controller.abort(), timeoutMs)
  }
  return {
    signal: controller.signal,
    cancel: () => {
      if (timer) clearTimeout(timer)
      controller.abort()
    },
  }
}
